import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Outlet, useNavigate } from 'react-router-dom'

import produce from 'immer'
import _ from 'lodash'
import { FilterMatchMode, FilterOperator, FilterService } from 'primereact/api'
import { Button } from 'primereact/button'
import { Column, ColumnFilterElementTemplateOptions } from 'primereact/column'
import { DataTableFilterMeta } from 'primereact/datatable'
import { MultiSelect, MultiSelectChangeEvent } from 'primereact/multiselect'

import { SearchField } from '../../../../../components/SearchField'
import {
  BooleanBodyTemplate,
  BooleanFilterTemplate,
} from '../../../../../components/tableTemplates'
import { useSearchParam } from '../../../../../hooks'
import { useColumnToggle } from '../../../../../hooks/useColumnToggle'
import { ConfigurableTable } from '../../../../../utils/wrapper'
import { useConfigContext } from '../../../../ui-config'
import { useAllSuppliers } from '../../../suppliers/data'
import { MasterDataDownloadButton } from '../../common/components'
import { useAllCompanyCodes } from '../../company-codes/data'
import {
  CompanyCode,
  MasterDataSupplier,
  requestDownloadSuppliers,
  useAllMasterDataSuppliers,
} from '../data'
import { MasterDataUpload } from './dialogs/upload'

const STORAGE_KEY = 'master-data-suppliers-visible-columns'
const TOGGLEABLE_FIELDS = [
  'Common.TaxNumber',
  'Common.SalesTaxID',
  'Address.Street',
  'Address.PostalCode',
  'Address.City',
  'Address.Country',
  'CompanyCodes',
  'CockpitStatus',
]

const defaultFilters: DataTableFilterMeta = {
  global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  'Common.DisplayId': {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
  },
  'Common.Company': {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
  },
  'Common.TaxNumber': {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
  },
  'Common.SalesTaxID': {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
  },
  'Address.Street': {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
  },
  'Address.PostalCode': {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
  },
  'Address.City': {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
  },
  'Address.Country': {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
  },
  CompanyCodes: {
    value: null,
    matchMode: FilterMatchMode.CUSTOM,
  },
  CockpitStatus: {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }],
  },
}

FilterService.register('custom_CompanyCodes', (value, filters) => {
  // If no filter is active don't filter
  if (filters === null || filters.length === 0) return true

  const rowCompanyNames =
    value?.map((companyCode: CompanyCode) => companyCode.Common.Company) || []
  const hasIntersections = _.intersection(rowCompanyNames, filters)

  return hasIntersections.length > 0
})

type MasterDataSupplierWithCockpitStatus = MasterDataSupplier & {
  CockpitStatus: boolean
}

export const Suppliers: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { uiConfig: config } = useConfigContext()
  const { navigationColumnHeader, show } = useColumnToggle(
    config.selfservices.masterDataManagement.suppliers.table,
    TOGGLEABLE_FIELDS,
    STORAGE_KEY,
    true,
  )
  const {
    data: suppliers,
    isLoading,
    error,
  } = useAllMasterDataSuppliers(true, true)
  const { data: activatedSuppliers } = useAllSuppliers()
  const { data: companyCodes } = useAllCompanyCodes()

  const [searchParam, setSearchParam] = useSearchParam('search')

  const [selectedItems, setSelectedItems] = useState<any | null>(null)

  const [filters, setFilters] = useState<DataTableFilterMeta>(defaultFilters)

  useEffect(() => {
    initFilters()
  }, [])

  useEffect(() => {
    // Reset selection when data changes (e.g., after deletion)
    setSelectedItems(null)
  }, [suppliers])

  const clearFilter = () => {
    initFilters()
  }

  const initFilters = () => {
    setFilters(defaultFilters)
    setSearchParam('')
  }

  useEffect(() => {
    onGlobalFilterChange(searchParam)
  }, [searchParam])

  const onGlobalFilterChange = (value: string) => {
    let _filters = JSON.parse(JSON.stringify(filters))

    _filters['global'].value = value
    setFilters(_filters)
    setSearchParam(value)
  }

  const handleAdd = () => navigate('add/')

  const headerActions = (
    <>
      <MasterDataDownloadButton
        label={t('masterDataManagement.suppliers.table.download.label')}
        downloadFunction={requestDownloadSuppliers}
        fileNamePrefix="suppliers"
        fileExtension="zip"
      />

      <MasterDataUpload />

      <Button
        label={t('masterDataManagement.suppliers.table.delete.label')}
        onClick={() =>
          handleDelete(
            selectedItems
              ?.map((item: MasterDataSupplier) => item.Id)
              .filter((id: string): id is string => !!id) || [],
          )
        }
        icon="pi pi-trash"
        outlined
        disabled={!selectedItems?.length}
      />

      <Button
        label={t('masterDataManagement.suppliers.table.dialog.button.add')}
        onClick={() => handleAdd()}
        icon="pi pi-plus"
        disabled={companyCodes?.length === 0}
        tooltip={
          companyCodes?.length === 0
            ? t(
                'masterDataManagement.suppliers.table.dialog.tooltip.companyCodesNeeded',
              )
            : undefined
        }
        tooltipOptions={{ showOnDisabled: true, position: 'top' }}
        visible={
          config.selfservices.masterDataManagement.suppliers.add.enabled !==
          false
        }
      />

      <SearchField
        searchValue={searchParam}
        setSearchValue={setSearchParam}
        placeholder={t(
          'masterDataManagement.suppliers.table.search.placeholder',
        )}
        classNameInput="surface-50"
      />
    </>
  )

  //  action template
  const handleEdit = (supplierId: string) => navigate('edit/' + supplierId)
  const handleInfo = (supplierId: string) => navigate('info/' + supplierId)
  const handleDelete = (supplierIds: string[]) =>
    navigate('delete/' + supplierIds.join(','))

  const actionBodyTemplate = (rowData: MasterDataSupplier) => {
    return (
      <div className="flex justify-content-end flex-nowrap">
        <Button
          text
          icon="pi pi-trash"
          severity="danger"
          rounded
          aria-label={t('masterDataManagement.suppliers.table.delete.label')}
          onClick={() => handleDelete([rowData.Id])}
        />
        <Button
          text
          icon="pi pi-info-circle"
          rounded
          aria-label={t(
            'masterDataManagement.suppliers.table.dialog.button.info',
          )}
          onClick={() => handleInfo(rowData.Id)}
          visible={
            config.selfservices.masterDataManagement.suppliers.info.enabled !==
            false
          }
        />
        <Button
          text
          icon="pi pi-pencil"
          rounded
          aria-label={t(
            'masterDataManagement.suppliers.table.dialog.button.edit',
          )}
          onClick={() => handleEdit(rowData.Id)}
          visible={
            config.selfservices.masterDataManagement.suppliers.edit.enabled !==
            false
          }
        />
      </div>
    )
  }

  const buttons = (
    <Column
      key="buttons"
      body={actionBodyTemplate}
      align="right"
      header={
        <div className="flex justify-content-end">{navigationColumnHeader}</div>
      }
    />
  )

  const companyCodesBodyTemplate = (
    rowData: MasterDataSupplier | undefined,
  ) => {
    const companies = rowData?.CompanyCodes?.map(
      companyCode => companyCode.Common.Company,
    )
    return <>{companies?.join(', ')}</>
  }

  const companyCodesFilterTemplate = (
    options: ColumnFilterElementTemplateOptions,
  ) => {
    return (
      <MultiSelect
        value={options.value}
        options={companyCodes}
        onChange={(e: MultiSelectChangeEvent) =>
          options.filterCallback(e.value)
        }
        optionLabel="Common.Company"
        optionValue="Common.Company"
        display="chip"
        placeholder={t(
          'masterDataManagement.suppliers.table.columns.companyCodes.filterPlaceholder',
        )}
        maxSelectedLabels={3}
        className="w-full md:w-20rem"
      />
    )
  }

  const updatedSuppliers = useMemo(
    () =>
      produce<MasterDataSupplierWithCockpitStatus[]>(
        suppliers as any,
        (draft: any) => {
          draft?.forEach((supplier: MasterDataSupplierWithCockpitStatus) => {
            const isInCockpit = activatedSuppliers?.some(
              activatedSupplier =>
                activatedSupplier.Id === supplier.Common.DisplayId,
            )

            supplier.CockpitStatus = !!isInCockpit
          })
        },
      ),
    [suppliers, activatedSuppliers],
  )

  const columns: JSX.Element[] = [
    <Column key="Common.DisplayId" field="Common.DisplayId" filter sortable />,
    <Column key="Common.Company" field="Common.Company" filter sortable />,
    show('Common.TaxNumber') ? (
      <Column key="Common.TaxNumber" field="Common.TaxNumber" filter sortable />
    ) : null,
    show('Common.SalesTaxID') ? (
      <Column
        key="Common.SalesTaxID"
        field="Common.SalesTaxID"
        dataType="numeric"
        filter
        sortable
      />
    ) : null,
    show('Address.Street') ? (
      <Column key="Address.Street" field="Address.Street" />
    ) : null,
    show('Address.PostalCode') ? (
      <Column
        key="Address.PostalCode"
        field="Address.PostalCode"
        filter
        sortable
      />
    ) : null,
    show('Address.City') ? (
      <Column key="Address.City" field="Address.City" />
    ) : null,
    show('Address.Country') ? (
      <Column key="Address.Country" field="Address.Country" filter sortable />
    ) : null,
    show('CompanyCodes') ? (
      <Column
        key="CompanyCodes"
        field="CompanyCodes"
        body={companyCodesBodyTemplate}
        filter
        filterElement={companyCodesFilterTemplate}
        sortable
        showFilterMatchModes={false}
      />
    ) : null,
    show('CockpitStatus') ? (
      <Column
        key="CockpitStatus"
        field="CockpitStatus"
        header={t(
          'masterDataManagement.suppliers.table.columns.cockpitStatus.header',
        )}
        body={(rowData: MasterDataSupplierWithCockpitStatus) =>
          BooleanBodyTemplate(rowData.CockpitStatus)
        }
        align="center"
        alignHeader="left"
        filter
        filterElement={BooleanFilterTemplate}
        sortable
      />
    ) : null,
  ].filter((c): c is JSX.Element => c !== null)

  return (
    <>
      <ConfigurableTable
        config={config.selfservices.masterDataManagement.suppliers.table}
        data={updatedSuppliers}
        headerActions={headerActions}
        defaultFilters={filters}
        updateDefaultFilters={setFilters}
        clearDefaultFilter={clearFilter}
        globalFilterFields={[
          'Common.DisplayId',
          'Common.Company',
          'Common.SalesTaxID',
        ]}
        globalFilterValue={searchParam}
        setGlobalFilterValue={setSearchParam}
        isLoading={isLoading}
        stateKey="table-master-data-management-suppliers"
        buttons={buttons}
        checkboxen
        selected={selectedItems}
        setSelected={setSelectedItems}
      >
        {columns}
      </ConfigurableTable>

      <Outlet />
    </>
  )
}
